import { Autocomplete, Box, Button, Chip, DialogActions, DialogContent, DialogTitle, Dropdown, FormControl, Input, Menu, MenuButton, MenuItem, Modal, ModalDialog, Typography } from "@mui/joy";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import React from "react";
import { Util } from "../Helpers/Util";
import { Mechanic } from "../Interfaces/Mechanic";
import PortalTableLayout from "../components/PortalTableLayout";
import WrappedButton from "../WrappedButton";
import WorkOrderDialog from "../Dialogs/WorkOrderDialog";
import BypassFiftyPercentPaymentDialog from "../Dialogs/BypassFiftyPercentPaymentDialog";
import WorkOrderHistoryDialog from "../Dialogs/WorkOrderHistoryDialog";
import SignatureCanvas from "react-signature-canvas";

export interface PackingList {
	BuildingName: string;
	InvoiceNumber: string;
	SerialNumber: string;
	Mechanic: string;
	InvoiceHeaderID: string;
	PackingListID: string;
	IsDropdownOpen: boolean;
	Notes: string;
	SignedDate: string;
	SignedDateRaw: Date | null;
	TypeOfWork: string;
	FiftyPercentPaid: string;
	FiftyPercentBypassedBy: string;
}

type SortColumn = "BuildingName" | "InvoiceNumber" | "SerialNumber" | "SignedDate" | "FiftyPercentPaid";
type SortDirection = "asc" | "desc";

const fiftyPercentSortRank = (paid: string) => {
	switch (paid) {
		case "No":
			return 0;
		case "Bypassed":
			return 1;
		case "Yes":
			return 2;
		default:
			return 3;
	}
};

const compareEstimateNumbers = (a: string, b: string) => {
	const numA = Number(a);
	const numB = Number(b);
	if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
		return numA - numB;
	}
	return a.localeCompare(b);
};

const fiftyPercentPaidCell = (paid: string, bypassedBy: string) => (
	<div>
		<Chip
			size="sm"
			variant="soft"
			color={paid === "Yes" ? "success" : paid === "Bypassed" ? "warning" : "danger"}>
			{paid}
		</Chip>
		{paid === "Bypassed" && bypassedBy && (
			<Typography level="body-xs" sx={{ mt: 0.5 }}>
				{bypassedBy}
			</Typography>
		)}
	</div>
);

export default function UnapprovedPackingList() {
	const [data, setData] = React.useState<Array<PackingList>>();
	const [mechanics, setMechanics] = React.useState<Array<Mechanic>>();
	const [signModal, setSignModal] = React.useState(false);
	const [packingList, setPackingList] = React.useState<String>();
	const [mechanic, setMechanic] = React.useState<String>();
	const [estimate, setEstimate] = React.useState<String>();
	const [buildingName, setBuildingName] = React.useState<String>();
	const [pageNumber, setPageNumber] = React.useState(0);
	const [pageSize, setPageSize] = React.useState(25);
	const [isSaving, setIsSaving] = React.useState(false);
	const [globalFilter, setGlobalFilter] = React.useState("");
	const [openDropdown, setOpenDropdown] = React.useState<Array<boolean>>([]);
	const [workOrderModal, setWorkOrderModal] = React.useState(false);
	const [currentPackingListId, setCurrentPackingListId] = React.useState<string>("");
	const [currentClientName, setCurrentClientName] = React.useState("");
	const [currentEstimateNumber, setCurrentEstimateNumber] = React.useState("");
	const [currentTypeOfWork, setCurrentTypeOfWork] = React.useState("");
	const [currentNotes, setCurrentNotes] = React.useState("");
	const [sortColumn, setSortColumn] = React.useState<SortColumn | null>(null);
	const [sortDirection, setSortDirection] = React.useState<SortDirection>("asc");
	const [historyModal, setHistoryModal] = React.useState(false);
	const [bypassModal, setBypassModal] = React.useState(false);

	let sigRef: any = null;

	const getMechanics = () => {
		fetch("User/GetPackingListAssignees", {
			method: "POST",
			cache: "no-cache",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({}),
		})
			.then((x) => x.json())
			.then((x) => setMechanics(x));
	};

	React.useEffect(() => {
		getData();
		getMechanics();
	}, []);

	const getData = () => {
		sigRef && sigRef.clear();
		fetch("PackingList/GetPackingLists", {
			method: "Post",
			cache: "no-cache",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ approved: false }),
		})
			.then((x) => x.json())
			.then((x) => {
				const arr: Array<boolean> = [];
				setData(
					x.map((y: any) => {
						arr.push(false);
						return {
							BuildingName: y.building_name,
							InvoiceNumber: y.invoice_number,
							SerialNumber: y.serial_number,
							Mechanic: y.mechanic_name,
							InvoiceHeaderID: y.invoice_header_id,
							PackingListID: y.packing_list_id,
							IsDropdownOpen: false,
							Notes: y.notes ?? "",
							SignedDate: y.signed_date ? new Date(y.signed_date).toLocaleDateString() : "",
							SignedDateRaw: y.signed_date ? new Date(y.signed_date) : null,
							TypeOfWork: y.proposal_line_item_type ?? "",
							FiftyPercentPaid: y.fifty_percent_paid ?? "No",
							FiftyPercentBypassedBy: y.fifty_percent_bypassed_by ?? "",
						} as PackingList;
					})
				);
				setOpenDropdown(arr);
			});
	};

	const logPackingListView = (packingListId: string, viewType: string) => {
		fetch("PackingList/LogPackingListView", {
			method: "POST",
			cache: "no-cache",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ packingListId, viewType }),
		});
	};

	const openWorkOrderDialog = (inv: PackingList) => {
		logPackingListView(inv.PackingListID, "work_order");
		setCurrentPackingListId(inv.PackingListID);
		setCurrentClientName(inv.BuildingName);
		setCurrentEstimateNumber(inv.InvoiceNumber);
		setCurrentTypeOfWork(inv.TypeOfWork);
		setCurrentNotes(inv.Notes);
		setWorkOrderModal(true);
	};

	const openHistoryDialog = (inv: PackingList) => {
		setCurrentPackingListId(inv.PackingListID);
		setCurrentEstimateNumber(inv.InvoiceNumber);
		setHistoryModal(true);
	};

	const openBypassDialog = (inv: PackingList) => {
		setCurrentPackingListId(inv.PackingListID);
		setCurrentEstimateNumber(inv.InvoiceNumber);
		setBypassModal(true);
	};

	const openPdf = (inv: PackingList) => {
		logPackingListView(inv.PackingListID, "pdf");
		fetch("PackingList/GetUnsignedPdf", {
			method: "POST",
			cache: "no-cache",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ estimateNumber: inv.InvoiceNumber }),
		})
			.then((x) => x.text())
			.then((x) => {
				var tab = window.open();
				if (tab) tab.location.href = document.location.origin + "/pdfs/" + x;
			});
	};

	const skipSignature = () => {
		setIsSaving(true);
		var dt = { packingListId: packingList, mechanicId: mechanic };
		fetch("PackingList/AssignPackingList", {
			method: "Post",
			cache: "no-cache",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(dt),
		}).then(() => {
			setSignModal(false);
			setIsSaving(false);
			getData();
		});
	};

	const save = () => {
		if (sigRef.isEmpty()) {
			alert("A signature is required.");
			return;
		}
		setIsSaving(true);
		var dt = {
			base64Image: sigRef.toDataURL(),
			packingListId: packingList,
			estimateNumber: estimate,
			mechanicId: mechanic,
			buildingName: buildingName,
		};
		fetch("PackingList/SavePackingList", {
			method: "Post",
			cache: "no-cache",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(dt),
		}).then(() => {
			setSignModal(false);
			setIsSaving(false);
			getData();
		});
	};

	const handleSort = (column: SortColumn) => {
		if (sortColumn === column) {
			setSortDirection(sortDirection === "asc" ? "desc" : "asc");
		} else {
			setSortColumn(column);
			setSortDirection("asc");
		}
	};

	const filteredData = data?.filter(
		(x) =>
			Util.isNullOrEmpty(globalFilter) ||
			x.BuildingName.toLowerCase().includes(globalFilter.toLowerCase()) ||
			x.InvoiceNumber.toString().includes(globalFilter)
	);

	const filteredCount = filteredData?.length ?? 0;

	const sortedData = filteredData?.sort((a, b) => {
		if (!sortColumn) return 0;
		let comparison = 0;
		if (sortColumn === "SignedDate") {
			const dateA = a.SignedDateRaw?.getTime() ?? 0;
			const dateB = b.SignedDateRaw?.getTime() ?? 0;
			comparison = dateA - dateB;
		} else if (sortColumn === "FiftyPercentPaid") {
			comparison = fiftyPercentSortRank(a.FiftyPercentPaid) - fiftyPercentSortRank(b.FiftyPercentPaid);
		} else if (sortColumn === "InvoiceNumber") {
			comparison = compareEstimateNumbers(a.InvoiceNumber, b.InvoiceNumber);
		} else {
			comparison = a[sortColumn].localeCompare(b[sortColumn]);
		}
		return sortDirection === "asc" ? comparison : -comparison;
	});

	const thisRows = sortedData?.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize);

	const sortableColumns: { key: SortColumn; label: string }[] = [
		{ key: "BuildingName", label: "Building Name" },
		{ key: "InvoiceNumber", label: "Estimate #" },
		{ key: "SerialNumber", label: "Serial #" },
		{ key: "SignedDate", label: "Signed Date" },
		{ key: "FiftyPercentPaid", label: "50% Paid" },
	];
	const columnCount = sortableColumns.length + 2;
	return (
		<>
			<WorkOrderDialog
				open={workOrderModal}
				packingListId={currentPackingListId}
				clientName={currentClientName}
				estimateNumber={currentEstimateNumber}
				typeOfWork={currentTypeOfWork}
				initialNotes={currentNotes}
				onClose={() => setWorkOrderModal(false)}
				onSaved={() => {
					setWorkOrderModal(false);
					getData();
				}}
			/>
			<WorkOrderHistoryDialog
				open={historyModal}
				packingListId={currentPackingListId}
				invoiceNumber={currentEstimateNumber}
				onClose={() => setHistoryModal(false)}
			/>
			<BypassFiftyPercentPaymentDialog
				open={bypassModal}
				packingListId={currentPackingListId}
				invoiceNumber={currentEstimateNumber}
				onClose={() => setBypassModal(false)}
				onSaved={() => {
					setBypassModal(false);
					getData();
				}}
			/>
			<Modal open={signModal}>
				<ModalDialog>
					<DialogTitle>Signature</DialogTitle>
					<DialogContent>
						<SignatureCanvas
							ref={(ref) => {
								sigRef = ref;
							}}
							penColor="black"
							canvasProps={{ width: "900", height: "400", style: { border: "1px solid black", borderRadius: 0 } }}
						/>
					</DialogContent>
					<DialogActions>
						<WrappedButton onClick={save} busy={isSaving}>
							Save Signature
						</WrappedButton>
						<WrappedButton onClick={skipSignature} busy={isSaving} color="warning">
							Skip Signature
						</WrappedButton>
						<Button onClick={() => setSignModal(false)} color="primary">
							Close
						</Button>
					</DialogActions>
				</ModalDialog>
			</Modal>
			<PortalTableLayout
				toolbar={
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 1.5,
							backgroundColor: "background.surface",
							border: "1px solid",
							borderColor: "divider",
							p: 1.5,
						}}>
						<Typography level="title-md" sx={{ m: 0, color: "text.secondary" }}>
							Pending Work Orders
						</Typography>
						<FormControl sx={{ minWidth: 180 }}>
							<Input
								name="Search"
								placeholder="Global Search"
								onChange={(event) => {
									setGlobalFilter(event.target.value);
									setPageNumber(0);
								}}
								value={globalFilter}
							/>
						</FormControl>
					</Box>
				}
				pagination={{
					count: filteredCount,
					page: pageNumber,
					rowsPerPage: pageSize,
					rowsPerPageOptions: [5, 10, 25, 50],
					onPageChange: (_event, newPage) => setPageNumber(newPage),
					onRowsPerPageChange: (event) => {
						setPageSize(parseInt(event.target.value, 10));
						setPageNumber(0);
					},
				}}>
				<Table stickyHeader size="small">
					<TableHead>
						<TableRow>
							{sortableColumns.slice(0, 3).map(({ key, label }) => (
								<TableCell key={key} sortDirection={sortColumn === key ? sortDirection : false}>
									<TableSortLabel
										active={sortColumn === key}
										direction={sortColumn === key ? sortDirection : "asc"}
										onClick={() => handleSort(key)}>
										{label}
									</TableSortLabel>
								</TableCell>
							))}
							<TableCell>Mechanic</TableCell>
							{sortableColumns.slice(3).map(({ key, label }) => (
								<TableCell key={key} sortDirection={sortColumn === key ? sortDirection : false}>
									<TableSortLabel
										active={sortColumn === key}
										direction={sortColumn === key ? sortDirection : "asc"}
										onClick={() => handleSort(key)}>
										{label}
									</TableSortLabel>
								</TableCell>
							))}
							<TableCell>Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{thisRows?.map((x) => (
							<TableRow key={x.PackingListID} hover sx={{ cursor: "pointer" }}>
								<TableCell>{x.BuildingName}</TableCell>
								<TableCell>{x.InvoiceNumber}</TableCell>
								<TableCell>{x.SerialNumber}</TableCell>
								<TableCell>
									<Autocomplete
										value={null}
										options={mechanics ?? []}
										getOptionLabel={(opt) => opt.full_name}
										openOnFocus
										placeholder="Select Mechanic"
										onChange={(event, value) => {
											setSignModal(true);
											setPackingList(x.PackingListID);
											setMechanic(value?.users_id);
											setEstimate(x.InvoiceNumber);
											setBuildingName(x.BuildingName);
										}}
									/>
								</TableCell>
								<TableCell>{x.SignedDate}</TableCell>
								<TableCell>{fiftyPercentPaidCell(x.FiftyPercentPaid, x.FiftyPercentBypassedBy)}</TableCell>
								<TableCell>
									<Dropdown>
										<MenuButton variant="plain" size="sm">
											<MoreVertIcon />
										</MenuButton>
										<Menu>
											<MenuItem onClick={() => openPdf(x)}>Open PDF</MenuItem>
											<MenuItem onClick={() => openWorkOrderDialog(x)}>Work Order</MenuItem>
											{x.FiftyPercentPaid === "No" && (
												<MenuItem onClick={() => openBypassDialog(x)}>Bypass 50% Payment</MenuItem>
											)}
											<MenuItem onClick={() => openHistoryDialog(x)}>View History</MenuItem>
										</Menu>
									</Dropdown>
								</TableCell>
							</TableRow>
						))}
						{!thisRows?.length && (
							<TableRow>
								<TableCell colSpan={columnCount} align="center" sx={{ py: 3 }}>
									No records found.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</PortalTableLayout>
		</>
	);
}
